/**
 * THE BASE — the ONE definition of how much of a check a single payer covers,
 * in minor units, from {@link GuestCheckView} (the shared balance helper's
 * projection) and nothing else.
 *
 * ── Why this lives in `packages/common` ─────────────────────────────────────
 * Two planes have to agree on it, and they are siblings:
 *
 *   * `apps/api`'s `POST /public/checks/:checkToken/pay` CHARGES this number
 *     (via `computeChargeBaseCents`, which is now a thin alias of this
 *     function), and
 *   * `apps/checkout`'s `CheckPayPage` SHOWS it to the guest before they press
 *     Pay, and derives the tip percentage from it.
 *
 * A second copy of this arithmetic is exactly the defect CLAUDE.md describes:
 * "a rule that lives in `client` is invisible to every server-side report, and
 * a second copy is how one document ends up showing two numbers that each look
 * correct." The rounding arms below (`even`'s last part, `items`' share of the
 * total) are where those two numbers would quietly disagree, on a surface that
 * moves real money.
 *
 * The SERVER remains the authority: it recomputes here after taking the
 * check's pay lock, against a balance re-read at that moment, and refuses or
 * caps accordingly. The page's copy is a preview of the same rule, never a
 * promise — and because it is the same rule, the preview and the charge differ
 * only by what genuinely changed at the table in between.
 *
 * Per mode (spec §4.4 step 3):
 *   full   — the whole balance.
 *   amount — the requested figure, CAPPED at the balance. Capping rather than
 *            refusing is deliberate: the page's figure can only be stale
 *            downward-safe, and charging less than asked never overcharges.
 *   even   — balance ÷ parts, with the LAST part taking the rounding cent, so
 *            the parts sum to exactly the balance: no cent is invented and
 *            none is lost.
 *   items  — the selected lines' share OF THE CHECK TOTAL, not their bare
 *            pre-tax line totals. A bill is subtotal + tax + service, and a
 *            guest paying "my two dishes" owes the tax and service those
 *            dishes attracted; charging bare line totals would systematically
 *            under-collect and leave the last payer holding everyone's tax.
 *            Selecting EVERY line charges the total exactly rather than a sum
 *            of rounded shares.
 *
 * ITEM CLAIMS: `check_item_claims`
 * (`packages/models/src/check_claims/CheckItemClaim.ts`) is written by the pay
 * route after an APPROVED `items`-mode payment. THIS function reads it — for
 * `items` mode ONLY — to EXCLUDE an already-claimed `orderItemGuid` from
 * `selectedCents`, never to refuse a payment. Its own invariant, verbatim:
 * "Claims are ADVISORY for UX — the money truth stays `paidAmount`, and a
 * claim never blocks a payment that fits the balance." The two are compatible,
 * not in tension, once "block" is read precisely:
 *
 *   - EXCLUDING a claimed item makes the base SMALLER. It is not a refusal —
 *     the payment for whatever remains still proceeds, still capped at the
 *     balance below, same as always.
 *   - The invariant is violated only by a check that turns a claim into an
 *     outright REFUSAL of a payment the balance could otherwise take. This
 *     function never does that: `full`/`even`/`amount` never consult claims at
 *     all (none of them name an `orderItemGuid` to key one on), and `items`
 *     mode only shrinks what THIS payer is charged for a dish someone else
 *     already covered — it never turns a fitting payment away.
 *   - The one refusal claims CAN cause — `ITEMS_ALREADY_CLAIMED` — is not
 *     "your payment doesn't fit," it is "you selected nothing left to claim,"
 *     which is exactly as true when computed from claims as it would be if the
 *     guest had submitted an empty selection. `full` and `amount` remain
 *     available on the SAME check even when every item is claimed.
 *
 * The browser passes NO claim set (it is not told which items others have
 * covered — the guest projection carries no claims), so its preview can only
 * be an over-estimate that the server then trims. That asymmetry is by
 * construction and is why `claimedItemGuids` defaults to empty rather than
 * being required.
 *
 * A base ABOVE the balance is refused, never clamped — except for `amount`,
 * whose cap means it can never reach that branch.
 */

import type { GuestCheckView } from './projectGuestCheck';

/** How much of the check one payer is covering. The wire body is a superset. */
export interface GuestCheckPaySelection {
  readonly mode: 'full' | 'items' | 'even' | 'amount';
  /** `items` only — the check lines this payer is covering. */
  readonly itemGuids?: readonly string[];
  /** `even` only — how many ways the CURRENT balance is being split. */
  readonly evenParts?: number;
  /** `even` only — which part (1-based). The LAST part takes the rounding cent. */
  readonly evenShare?: number;
  /** `amount` only — what the payer asked to pay, capped at the balance. */
  readonly amountCents?: number;
}

/** Why a base could not be computed. Each maps to exactly one wire refusal. */
export type GuestCheckBaseRefusalCode =
  | 'NOTHING_DUE'
  | 'UNKNOWN_ITEM'
  | 'AMOUNT_EXCEEDS_BALANCE'
  /**
   * `items` mode only: every requested `orderItemGuid` is already covered by
   * an earlier payment (`claimedItemGuids`). Distinguishable from
   * `NOTHING_DUE` on purpose — this check can still have money owed on it;
   * this SELECTION just names nothing of it. A guest who picked well and lost
   * a race to another payer at the table is not an error case, and the payer's
   * page should offer another mode rather than show a generic failure.
   */
  | 'ITEMS_ALREADY_CLAIMED';

export type GuestCheckBaseOutcome =
  | {
      ok: true;
      baseCents: number;
      /**
       * `items` mode only: the `orderItemGuid`s actually priced into
       * `baseCents`, AFTER excluding any already-claimed ones. The route
       * writes a claim for exactly these — never for an item the payer named
       * but did not end up paying for. `undefined` for every other mode,
       * which carries no per-item breakdown to claim.
       */
      chargedItemGuids?: readonly string[];
    }
  | { ok: false; code: GuestCheckBaseRefusalCode; message: string };

/**
 * Compute the base one payer covers. Pure — the same inputs always give the
 * same answer on either plane, which is the whole point of it living here.
 *
 * @param view              the guest-safe projection of the check being paid.
 * @param selection         which part of it this payer is covering.
 * @param claimedItemGuids  `items` mode only — the `orderItemGuid`s already
 *                          covered by an earlier APPROVED payment on THIS
 *                          check. The API reads these fresh, after acquiring
 *                          the check's pay lock (a claim write runs in the
 *                          SAME transaction as that lock's release, so holding
 *                          the lock is proof every earlier claim has
 *                          committed). The browser has none and passes none.
 */
export function computeGuestCheckBaseCents(
  view: GuestCheckView,
  selection: GuestCheckPaySelection,
  claimedItemGuids: ReadonlySet<string> = new Set(),
): GuestCheckBaseOutcome {
  const balance = view.balanceCents;
  if (balance <= 0) {
    return { ok: false, code: 'NOTHING_DUE', message: 'This check has nothing left to pay.' };
  }

  let base: number;
  // `items` mode only — the guids actually priced into `base`, after
  // excluding any already-claimed ones. `undefined` for every other mode.
  let chargedItemGuids: readonly string[] | undefined;
  switch (selection.mode) {
    case 'full':
      base = balance;
      break;
    case 'amount':
      base = Math.min(selection.amountCents ?? 0, balance);
      break;
    case 'even': {
      const parts = selection.evenParts ?? 1;
      const share = selection.evenShare ?? 1;
      const perPart = Math.floor(balance / parts);
      // The last part absorbs the remainder, so Σ parts === balance exactly.
      base = share === parts ? balance - perPart * (parts - 1) : perPart;
      break;
    }
    case 'items': {
      const wanted = new Set(selection.itemGuids ?? []);
      let selectedCents = 0;
      let allLinesCents = 0;
      const seen = new Set<string>();
      // The `orderItemGuid`s actually priced into `selectedCents` — an
      // already-claimed one is `seen` (it is real, so it is never
      // UNKNOWN_ITEM) but excluded here, per the file header.
      const charged: string[] = [];
      for (const line of view.lines) {
        allLinesCents += line.lineTotalCents;
        if (wanted.has(line.orderItemGuid)) {
          seen.add(line.orderItemGuid);
          if (claimedItemGuids.has(line.orderItemGuid)) continue;
          selectedCents += line.lineTotalCents;
          charged.push(line.orderItemGuid);
        }
      }
      // A guid this check does not carry is REFUSED, never dropped: dropping
      // it charges for less than the guest selected and tells them nothing.
      // Checked BEFORE the already-claimed outcome below: "this check has no
      // such item" is a stronger problem than "someone already covered it."
      for (const guid of wanted) {
        if (!seen.has(guid)) {
          return {
            ok: false,
            code: 'UNKNOWN_ITEM',
            message: 'One of the selected items is no longer on this check. Please reload and try again.',
          };
        }
      }
      // Every requested item is real (the loop above proved it) and every one
      // of them was excluded above — computed from `wanted` directly, never
      // inferred from `selectedCents === 0`, so a genuinely $0 line (a comp)
      // is never misreported as "already claimed."
      if (charged.length === 0) {
        return {
          ok: false,
          code: 'ITEMS_ALREADY_CLAIMED',
          message: 'Someone already covered everything you selected. Pick something else, or pay the remaining balance another way.',
        };
      }
      if (allLinesCents <= 0) {
        return {
          ok: false,
          code: 'NOTHING_DUE',
          message: 'This check has no items to split.',
        };
      }
      base =
        selectedCents === allLinesCents
          ? view.totalCents
          : Math.round((view.totalCents * selectedCents) / allLinesCents);
      chargedItemGuids = charged;
      break;
    }
  }

  if (base <= 0) {
    return { ok: false, code: 'NOTHING_DUE', message: 'This check has nothing left to pay.' };
  }
  if (base > balance) {
    return {
      ok: false,
      code: 'AMOUNT_EXCEEDS_BALANCE',
      message: 'That is more than is left on this check. Someone may have already paid part of it — please reload.',
    };
  }
  return { ok: true, baseCents: base, ...(chargedItemGuids ? { chargedItemGuids } : {}) };
}
